import React, { Dispatch, FC, FormEvent, Fragment, SetStateAction, useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  TextField,
} from '@mui/material';

import styles from './AddUserDialog.module.scss';
import { UserService } from '../UserService';
import AlertDialog from 'dialogs/AlertDialog/AlertDialog';
import { AlertConfigModel } from 'dialogs/AlertDialog/AlertConfigModel';

export interface AddUserDialogProps {
  setOpen: Dispatch<SetStateAction<boolean>>;
  onUserCreation: () => void;
}

export const AddUserDialog: FC<AddUserDialogProps> = (
  {
    setOpen,
    onUserCreation
  }
) => {

  const [currentAlert, setCurrentAlert] = useState<AlertConfigModel | null>(null);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('')
  const [name, setName] = useState<string>('')
  const [surname, setSurname] = useState<string>('')
  const [submitError, setSubmitError] = useState<string>('')

  const clearStates = (): void => {
    setName('');
    setSurname('');
    setEmail('');
    setSubmitError('');
  };

  const closeHandler = (event?: Object, reason?: any): void => {
    if (reason && reason === 'backdropClick') return;

    clearStates();
    setOpen(false);
  };

  const inviteUserHandler = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault(); // Prevent the default form submission behavior, which sends a request

    if (!name) {
      setSubmitError('Missing name');
      return;
    }

    if (!surname) {
      setSubmitError('Missing surname');
      return;
    }

    if (!email) {
      setSubmitError('Missing email');
      return;
    }

    const user = {
      name: name,
      surname: surname,
      email: email,
    };

    try {
      const creationResult = await UserService.create(user);

      if (!creationResult) {
        throw new Error('Error creating user.');
      }

      clearStates();
      onUserCreation();
      closeHandler();
    } catch (error) {
      console.error('Failed at creating user:', error);
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed at inviting user. Please try again, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    }
  }

  const invitationForm: JSX.Element = (
    <Fragment>
      <form onSubmit={inviteUserHandler} className={styles.userInvitationForm}>
        <FormControl className={styles.userInvitationFormControl}>
          <FormLabel required>Name</FormLabel>
          <TextField
            autoFocus
            required
            size="small"
            className={styles.formTextField}
            value={name}
            onChange={event => setName(event.target.value)}
          ></TextField>
        </FormControl>

        <FormControl className={styles.userInvitationFormControl}>
          <FormLabel required>Surname</FormLabel>
          <TextField
            required
            size="small"
            className={styles.formTextField}
            value={surname}
            onChange={event => setSurname(event.target.value)}
          ></TextField>
        </FormControl>

        <FormControl className={styles.userInvitationFormControl}>
          <FormLabel required>Email</FormLabel>
          <TextField
            required
            size="small"
            type="email"
            className={styles.formTextField}
            value={email}
            onChange={event => setEmail(event.target.value)}
          ></TextField>
        </FormControl>

        <br />
        {submitError && <Fragment><Alert severity="warning">{submitError}</Alert><br /></Fragment>}

        <DialogActions className={styles.formButtons}>
          <Button
            onClick={closeHandler}
            color="error"
            variant="outlined"
          >
            Cancel
          </Button>

          <Button
            type={'submit'}
            color={'primary'}
            variant="contained"
          >
            Invite
          </Button>
        </DialogActions>
      </form>
    </Fragment>
  );

  return (
    <Fragment>
      <Dialog
        className={styles.AddUserDialog}
        open={true}
        onClose={closeHandler}
      >
        <DialogTitle>Add User</DialogTitle>
        <DialogContent dividers={true}>
          {invitationForm}
        </DialogContent>
      </Dialog>

      { isAlertDialogOpen && currentAlert &&
        <AlertDialog
          onClose={() => {
            setIsAlertDialogOpen(false);
            setCurrentAlert(null);
          }}
          title={currentAlert.title}
          message={currentAlert.message}
        ></AlertDialog>
      }
    </Fragment>
  );
}
